<template>
  <div class="relative">
    <button
      @click.stop="toggleNotifications"
      dusk="notifications-dropdown"
      type="button"
      class="hover:text-primary-500 focus:outline-none focus:ring ring-primary-200 dark:ring-gray-600 rounded h-10 w-10 flex items-center justify-center"
    >
      <svg
        width="18"
        height="20"
        viewBox="0 0 18 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M17.1892 12.0608L16.0592 10.1808C15.8092 9.7708 15.5892 8.9808 15.5892 8.5008V6.63078C15.5892 3.00078 12.6392 0.0507812 9.0192 0.0507812C5.38923 0.0607812 2.43923 3.00078 2.43923 6.63078V8.4908C2.43923 8.9708 2.21923 9.7608 1.97923 10.1708L0.849226 12.0508C0.419226 12.7808 0.319226 13.6108 0.589226 14.3308C0.859226 15.0608 1.46923 15.6408 2.26923 15.9008C3.34923 16.2608 4.43923 16.5208 5.54923 16.7108C5.65923 16.7308 5.76923 16.7408 5.87923 16.7608C6.01923 16.7808 6.16923 16.8008 6.31923 16.8208C6.57923 16.8608 6.83923 16.8908 7.1092 16.9108C7.7392 16.9708 8.3792 17.0008 9.0192 17.0008C9.6492 17.0008 10.2792 16.9708 10.8992 16.9108C11.1292 16.8908 11.3592 16.8708 11.5792 16.8408C11.7592 16.8208 11.9392 16.8008 12.1192 16.7708C12.2292 16.7608 12.3392 16.7408 12.4492 16.7208C13.5692 16.5408 14.6792 16.2608 15.7592 15.9008C16.5292 15.6408 17.1192 15.0608 17.3992 14.3208C17.6792 13.5708 17.5992 12.7508 17.1892 12.0608ZM9.7492 8.0008C9.7492 8.4208 9.4092 8.7608 8.9892 8.7608C8.5692 8.7608 8.2292 8.4208 8.2292 8.0008V4.90078C8.2292 4.48078 8.5692 4.14078 8.9892 4.14078C9.4092 4.14078 9.7492 4.48078 9.7492 4.90078V8.0008ZM11.8297 18.01C11.4097 19.17 10.2997 20 8.9997 20C8.2097 20 7.4297 19.68 6.87969 19.11C6.55969 18.81 6.31969 18.41 6.17969 18C6.30969 18.02 6.43969 18.03 6.57969 18.05C6.80969 18.08 7.0497 18.11 7.2897 18.13C7.8597 18.18 8.4397 18.21 9.0197 18.21C9.5897 18.21 10.1597 18.18 10.7197 18.13C10.9297 18.11 11.1397 18.1 11.3397 18.07C11.4997 18.05 11.6597 18.03 11.8297 18.01Z"
          fill="#929EAE"
        />
      </svg>

      <span
        v-if="unreadNotifications"
        class="absolute border-[3px] border-white dark:border-gray-800 top-[3px] right-[4px] inline-block bg-red-500 rounded-full w-4 h-4"
      />
    </button>
  </div>

  <teleport to="#notifications">
    <div v-if="notificationsShown" class="fixed flex inset-0">
      <div
        @click="toggleNotifications"
        class="absolute inset-0 bg-gray-600 dark:bg-gray-900 opacity-75"
        dusk="notifications-backdrop"
      />

      <div
        class="relative divide-y divide-gray-200 dark:divide-gray-700 shadow bg-gray-100 dark:bg-gray-800 w-[20rem] ml-auto border-b border-gray-200 dark:border-gray-700 overflow-x-hidden overflow-y-scroll"
      >
        <nav
          v-if="notifications.length > 0"
          class="bg-white dark:bg-gray-800 flex items-center h-14 px-4"
        >
          <Heading :level="3" class="ml-1">{{ __('Notifications') }}</Heading>
          <LinkButton @click="markAllNotificationsAsRead" class="ml-auto">
            {{ __('Mark all as Read') }}
          </LinkButton>
        </nav>

        <div
          v-if="notifications.length > 0"
          class="divide-y divide-gray-200 dark:divide-gray-600"
          dusk="notifications-content"
        >
          <div
            v-for="notification in notifications"
            :key="notification.id"
            class="dark:border-gray-600"
          >
            <!-- Leave the extra div below, it allows the side border to work correctly -->
            <div
              class="border-l-4"
              :class="{
                'border-white dark:border-gray-800': notification.read_at,
                'border-red-500 dark:border-red-500': !notification.read_at,
              }"
            >
              <component
                :is="notification.component || `MessageNotification`"
                :notification="notification"
                @delete-notification="() => deleteNotification(notification.id)"
                @mark-as-read="() => markNotificationAsRead(notification.id)"
              />
            </div>
          </div>
        </div>

        <div v-else class="py-12">
          <p class="text-center">
            <svg
              class="inline-block text-gray-300 dark:text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              width="65"
              height="51"
              viewBox="0 0 65 51"
            >
              <path
                class="fill-current"
                d="M56 40h2c.552285 0 1 .447715 1 1s-.447715 1-1 1h-2v2c0 .552285-.447715 1-1 1s-1-.447715-1-1v-2h-2c-.552285 0-1-.447715-1-1s.447715-1 1-1h2v-2c0-.552285.447715-1 1-1s1 .447715 1 1v2zm-5.364125-8H38v8h7.049375c.350333-3.528515 2.534789-6.517471 5.5865-8zm-5.5865 10H6c-3.313708 0-6-2.686292-6-6V6c0-3.313708 2.686292-6 6-6h44c3.313708 0 6 2.686292 6 6v25.049375C61.053323 31.5511 65 35.814652 65 41c0 5.522847-4.477153 10-10 10-5.185348 0-9.4489-3.946677-9.950625-9zM20 30h16v-8H20v8zm0 2v8h16v-8H20zm34-2v-8H38v8h16zM2 30h16v-8H2v8zm0 2v4c0 2.209139 1.790861 4 4 4h12v-8H2zm18-12h16v-8H20v8zm34 0v-8H38v8h16zM2 20h16v-8H2v8zm52-10V6c0-2.209139-1.790861-4-4-4H6C3.790861 2 2 3.790861 2 6v4h52zm1 39c4.418278 0 8-3.581722 8-8s-3.581722-8-8-8-8 3.581722-8 8 3.581722 8 8 8z"
              />
            </svg>
          </p>

          <p class="mt-3 text-center">
            {{ __('There are no new notifications.') }}
          </p>

          <p class="mt-6 px-4 text-center">
            <DefaultButton @click="toggleNotifications"
              >{{ __('Close') }}
            </DefaultButton>
          </p>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const { mapMutations, mapActions, mapGetters } = createNamespacedHelpers('nova')

export default {
  created() {
    this.fetchNotifications()
  },

  watch: {
    notificationsShown(newValue) {
      if (newValue == true) {
        document.body.classList.add('overflow-y-hidden')
        return
      }

      document.body.classList.remove('overflow-y-hidden')
    },
  },

  mounted() {
    Nova.$on('refresh-notifications', () => this.fetchNotifications())
  },

  beforeUnmount() {
    document.body.classList.remove('overflow-y-hidden')
  },

  methods: {
    ...mapMutations(['toggleMainMenu', 'toggleNotifications']),
    ...mapActions([
      'fetchNotifications',
      'deleteNotification',
      'markNotificationAsRead',
      'markAllNotificationsAsRead',
    ]),
  },

  computed: {
    ...mapGetters([
      'mainMenuShown',
      'notificationsShown',
      'notifications',
      'unreadNotifications',
    ]),
  },
}
</script>
